// components/Sidebar/RightSidebar.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Sparkles, ListChecks, Lightbulb, MessageCircle, History, Clock, ChevronLeft, User, Bot, Lock, MoreVertical, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

const RightSidebar = () => {
  const [credits, setCredits] = useState(0);
  const [maxCredits, setMaxCredits] = useState(1000);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [planName, setPlanName] = useState('Free');
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [providerName, setProviderName] = useState('');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [tasks, setTasks] = useState({ pending: [], in_progress: [], completed: [] });
  const [isTasksLoading, setIsTasksLoading] = useState(true);
  
  // Chat credits state
  const [chatCredits, setChatCredits] = useState(0);
  const [chatCreditsUsed, setChatCreditsUsed] = useState(0);
  const [chatCreditsTotal, setChatCreditsTotal] = useState(0);
  const [chatResetAt, setChatResetAt] = useState(null);
  
  // Chat history state
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [openChatMenuId, setOpenChatMenuId] = useState(null);
  const [deletingChatId, setDeletingChatId] = useState(null);
  
  const navigate = useNavigate();
  const { frondId } = useParams();
  let authToken = localStorage.getItem("token");

  // Fetch subscription and credit data
  const fetchSubscriptionData = async () => {
    if (!authToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        '/billing/subscription-status',
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Accept': 'application/json',
          },
        }
      );
      
      console.log('Subscription API Response:', response.data);
      
      const data = response.data?.data || {};
      
      // Check if user is premium
      const isUserPremium = data.plan_slug === 'premium' || 
                           data.plan_name?.toLowerCase() === 'premium' ||
                           data.has_active_subscription === true;
      
      setHasActiveSubscription(data.has_active_subscription || false);
      setSubscriptionPlan(data.plan_slug || 'free');
      setPlanName(data.plan_name || 'Free');
      setIsPremium(isUserPremium);
      
      // General credits
      const creditsTotal = parseFloat(data.credits_total) || 0;
      const creditsRemaining = parseFloat(data.credits_remaining) || 0;
      const creditsUsedCount = parseFloat(data.credits_used) || 0;
      
      setCredits(creditsRemaining);
      setMaxCredits(creditsTotal || 600);
      setCreditsUsed(creditsUsedCount);
      
      // Handle provider name
      if (data.provider) {
        let provider = data.provider.toLowerCase();
        if (provider === 'stripe') {
          setProviderName('Stripe');
        } else if (provider === 'apple') {
          setProviderName('Apple');
        } else if (provider === 'google_play') {
          setProviderName('Google Play');
        } else {
          setProviderName(provider.charAt(0).toUpperCase() + provider.slice(1));
        }
      }
      
      // Chat credits - fetch separately if needed
      if (isUserPremium) {
        try {
          const chatResponse = await axios.get(
            '/chat-credits/status',
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
                'Accept': 'application/json',
              },
            }
          );
          
          const chatData = chatResponse.data?.data || {};
          setChatCredits(chatData.chat_credits_remaining || 0);
          setChatCreditsTotal(chatData.chat_credits_total || 400);
          setChatCreditsUsed(chatData.chat_credits_used || 0);
          setChatResetAt(chatData.reset_at || null);
        } catch (chatError) {
          console.error('Failed to fetch chat credits:', chatError);
          setChatCredits(0);
          setChatCreditsTotal(0);
          setChatCreditsUsed(0);
          setChatResetAt(null);
        }
      } else {
        setChatCredits(0);
        setChatCreditsTotal(0);
        setChatCreditsUsed(0);
        setChatResetAt(null);
      }
      
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      setCredits(0);
      setMaxCredits(600);
      setSubscriptionPlan('free');
      setPlanName('Free');
      setIsPremium(false);
      setHasActiveSubscription(false);
      setProviderName('');
      setChatCredits(0);
      setChatCreditsTotal(0);
      setChatCreditsUsed(0);
      setChatResetAt(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch tasks
  const fetchTasks = async () => {
    if (!authToken) {
      setIsTasksLoading(false);
      return;
    }

    setIsTasksLoading(true);
    try {
      const response = await axios.get(
        '/mytasks',
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const responseData = response.data?.response?.data || {};
      
      const pendingTasks = responseData.pending || [];
      const inProgressTasks = responseData.in_progress || [];
      const completedTasks = responseData.completed || [];

      setTasks({
        pending: pendingTasks,
        in_progress: inProgressTasks,
        completed: completedTasks
      });

    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks({ pending: [], in_progress: [], completed: [] });
    } finally {
      setIsTasksLoading(false);
    }
  };

  // Fetch chat history
  const fetchChatHistory = async () => {
    if (!authToken || !isPremium) {
      return;
    }

    setIsChatHistoryLoading(true);
    try {
      const response = await axios.get(
        '/chats',
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const chats = response.data || [];
      setChatHistory(chats);
      console.log('Chat history fetched:', chats);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      setChatHistory([]);
    } finally {
      setIsChatHistoryLoading(false);
    }
  };

  // Fetch chat messages
  const fetchChatMessages = async (chatId) => {
    if (!authToken || !isPremium) {
      return;
    }

    setIsMessagesLoading(true);
    try {
      const response = await axios.get(
        `/chat/${chatId}/messages`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const data = response.data || {};
      setSelectedChat(data.chat || null);
      setChatMessages(data.messages || []);
      console.log('Chat messages fetched:', data);
    } catch (error) {
      console.error('Failed to fetch chat messages:', error);
      setChatMessages([]);
    } finally {
      setIsMessagesLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();
    const interval = setInterval(fetchSubscriptionData, 300000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    if (isPremium && activeTab === 'chats') {
      fetchChatHistory();
    }
  }, [isPremium, activeTab]);

  const getPlanDisplayName = () => {
    if (isPremium) return 'Premium';
    if (subscriptionPlan === 'pro') return 'Pro';
    if (subscriptionPlan === 'starter') return 'Starter';
    return planName || 'Free';
  };

  const getPlanColor = () => {
    if (isPremium) return 'from-yellow-500 to-orange-500';
    if (subscriptionPlan === 'pro') return 'from-blue-500 to-purple-500';
    if (subscriptionPlan === 'starter') return 'from-green-500 to-teal-500';
    return 'from-gray-500 to-gray-400';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const handleChatClick = (chatId) => {
    fetchChatMessages(chatId);
  };

  const handleDeleteChat = async (chatId) => {
    if (!authToken || !chatId || deletingChatId) {
      return;
    }

    setDeletingChatId(chatId);
    try {
      const response = await axios.delete(
        `/chat/${chatId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setChatHistory((prevChats) =>
        prevChats.filter((chat) => chat.id !== chatId)
      );

      if (selectedChat?.id === chatId) {
        handleBackToChatList();
      }

      setOpenChatMenuId(null);
      toast.success(response.data?.message || "Chat deleted successfully", {
        position: "bottom-right",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete chat", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setDeletingChatId(null);
    }
  };

  const handleBackToChatList = () => {
    setSelectedChat(null);
    setChatMessages([]);
  };

  // Get all active tasks
  const activeTasks = [...tasks.pending, ...tasks.in_progress];
  const completedTasks = tasks.completed;
  const totalTasks = activeTasks.length + completedTasks.length;

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-lightWhite/30 via-offWhite/30 to-lightWhite/30">
        <div className="rounded-[40px] p-6 bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff]">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mainOrange"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-lightWhite/30 via-offWhite/30 to-lightWhite/30">
      {/* AI Credits Card */}
      <div className="rounded-[40px] p-6 bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#ff9d6c] via-[#ea3c70] to-[#c71d5b] shadow-[0_14px_30px_rgba(216,49,98,0.34),inset_0_1px_0_rgba(255,255,255,0.45)]">
              <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/35 via-white/10 to-transparent"></span>
              <Sparkles className="relative z-10 h-6 w-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-[#594044] text-md">Credits</h3>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${getPlanColor()} text-white`}>
                  {getPlanDisplayName()}
                </span>
              </div>
              <p className="text-xs text-[#594044]/60 uppercase tracking-wider">
                {creditsUsed} Credits Used
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${hasActiveSubscription ? 'bg-green-500' : 'bg-gray-400'}`}></span>
          </div>
        </div>

        {/* General Credits Progress Bar */}
        <div className="relative mb-3">
          <div className="w-full h-3 bg-Grey rounded-full overflow-hidden shadow-inner">
            <div 
              className="h-full bg-gradient-to-r from-mainOrange via-mainPink to-mainOrange rounded-full transition-all duration-500 bg-[length:200%] animate-shimmer"
              style={{ width: `${maxCredits > 0 ? (credits / maxCredits) * 100 : 0}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-[#594044]/60">{creditsUsed} used</span>
            <span className="text-xs text-[#594044]/60">{credits} remaining</span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <span className="text-2xl font-bold text-[#594044]">
            {credits} <span className="text-sm font-normal text-[#594044]/60">/ {maxCredits}</span>
          </span>
          <span className="text-xs font-medium text-[#594044]/60">
            {providerName}
          </span>
        </div>

        {/* Chat Credits Section - ONLY for Premium users */}
        {isPremium && (
          <div className="mt-4 pt-4 border-t border-royalBlue/10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-mainPink" />
                <span className="text-sm font-medium text-[#594044]">AI Chat Credits</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                  Premium
                </span>
              </div>
            </div>
            
            {/* Chat Credits Progress Bar */}
            <div className="relative mb-2">
              <div className="w-full h-2.5 bg-Grey rounded-full overflow-hidden shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 rounded-full transition-all duration-500 bg-[length:200%] animate-shimmer"
                  style={{ width: `${chatCreditsTotal > 0 ? (chatCredits / chatCreditsTotal) * 100 : 0}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-[#594044]/60">{chatCreditsUsed} used</span>
                <span className="text-xs text-[#594044]/60">{chatCredits} remaining</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-[#594044]">
                {chatCredits} <span className="text-sm font-normal text-[#594044]/60">/ {chatCreditsTotal}</span>
              </span>
              <span className="text-xs font-medium text-[#594044]/60">
                AI Conversations
              </span>
            </div>
            
            <div className="mt-2 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-2.5 border border-yellow-200/30">
              <p className="text-xs text-[#594044]/70 flex items-center gap-1.5">
                <span>💬</span> 
                {chatCredits > 0 ? (
                  <>You have <span className="font-medium text-orange-600">{chatCredits} chat credits</span> remaining</>
                ) : (
                  <span className="text-red-500 font-medium">No chat credits remaining. Reset {chatResetAt ? formatDate(chatResetAt) : 'soon'}.</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Status Message */}
        <div className="bg-gradient-to-r from-lightWhite to-offWhite rounded-2xl p-3 flex items-start gap-3 border border-royalBlue/10 mt-4">
          <p className="text-xs text-[#594044]">
            {isPremium ? (
              <>✨ <span className="font-small text-[#b50f4a]">{planName} Plan</span> - {credits} credits for projects, Tasks, images</>
            ) : hasActiveSubscription ? (
              <>🚀 <span className="font-small text-[#b50f4a]">{planName} Plan</span> - {credits} credits for projects, Tasks, images</>
            ) : (
              <>🔄 <span className="font-small text-[#b50f4a]">Subscribe</span> to access AI features and start creating</>
            )}
          </p>
        </div>

        {/* Upgrade to Premium button - Hide for premium users */}
        {!isPremium && hasActiveSubscription && (
          <button 
            onClick={() => navigate('/pricing')}
            className="w-full mt-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full hover:shadow-lg transition-all duration-300"
          >
            Upgrade to Premium for AI Chat →
          </button>
        )}
      </div>

      {/* Tasks & Chat History Section with Tabs */}
      <div className="rounded-[40px] p-6 bg-white/80 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300">
        {/* Tab Headers - Hide when viewing a specific chat */}
        {!selectedChat && (
          <div className="flex items-center gap-2 mb-5 border-b border-gray-100 pb-3">
            <div className="flex items-center gap-2 flex-1">
              <div className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#6b9cff] via-[#4f6eff] to-[#2f4ed8] shadow-[0_10px_20px_rgba(79,110,255,0.25)]">
                <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/35 via-white/10 to-transparent"></span>
                {activeTab === 'tasks' ? (
                  <ListChecks className="relative z-10 h-5 w-5 text-white" />
                ) : (
                  <History className="relative z-10 h-5 w-5 text-white" />
                )}
              </div>
              <div className="flex gap-1 bg-gray-100/50 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-300 ${
                    activeTab === 'tasks'
                      ? 'bg-white text-[#594044] shadow-sm'
                      : 'text-[#594044]/60 hover:text-[#594044]'
                  }`}
                >
                  Tasks {totalTasks > 0 && `(${activeTasks.length})`}
                </button>
                <button
                  onClick={() => setActiveTab('chats')}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-300 flex items-center gap-1 ${
                    activeTab === 'chats'
                      ? 'bg-white text-[#594044] shadow-sm'
                      : 'text-[#594044]/60 hover:text-[#594044]'
                  }`}
                >
                  <History className="h-3 w-3" />
                  Chats {chatHistory.length > 0 && `(${chatHistory.length})`}
                  {!isPremium && (
                    <span className="ml-1 text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-yellow-400/20 text-yellow-700">
                      PREMIUM
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'tasks' && !selectedChat ? (
          // Tasks Content
          <>
            {isTasksLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mainOrange"></div>
              </div>
            ) : totalTasks === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-[#594044]/60">No tasks assigned</p>
                <p className="text-xs text-[#594044]/40 mt-1">Tasks assigned to you will appear here</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {/* Pending Tasks */}
                {tasks.pending.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      Pending ({tasks.pending.length})
                    </h4>
                    {tasks.pending.map((task, idx) => (
                      <div key={task?.task?.id || idx} className="flex items-start gap-3 group mb-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full bg-blue-500 shadow-md mt-1.5`}></div>
                          {idx < tasks.pending.length - 1 && (
                            <div className="w-0.5 h-6 bg-gray-200 mt-1"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#594044] text-sm truncate">
                            {task?.task?.title || 'Untitled Task'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {task?.project_name && (
                              <span className="text-xs text-[#594044]/40">
                                📁 {task.project_name}
                              </span>
                            )}
                            {task?.task?.due_date && (
                              <span className="text-xs text-[#594044]/40">
                                📅 {formatDate(task.task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* In Progress Tasks */}
                {tasks.in_progress.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      In Progress ({tasks.in_progress.length})
                    </h4>
                    {tasks.in_progress.map((task, idx) => (
                      <div key={task?.task?.id || idx} className="flex items-start gap-3 group mb-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full bg-orange-500 shadow-md mt-1.5`}></div>
                          {idx < tasks.in_progress.length - 1 && (
                            <div className="w-0.5 h-6 bg-gray-200 mt-1"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#594044] text-sm truncate">
                            {task?.task?.title || 'Untitled Task'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {task?.project_name && (
                              <span className="text-xs text-[#594044]/40">
                                📁 {task.project_name}
                              </span>
                            )}
                            {task?.task?.due_date && (
                              <span className="text-xs text-[#594044]/40">
                                📅 {formatDate(task.task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Completed Tasks */}
                {completedTasks.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      Completed ({completedTasks.length})
                    </h4>
                    {completedTasks.slice(0, 3).map((task, idx) => (
                      <div key={task?.task?.id || idx} className="flex items-start gap-3 group mb-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full bg-green-500 shadow-md mt-1.5`}></div>
                          {idx < Math.min(completedTasks.length, 3) - 1 && (
                            <div className="w-0.5 h-6 bg-gray-200 mt-1"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#594044]/60 text-sm truncate line-through">
                            {task?.task?.title || 'Untitled Task'}
                          </p>
                          {task?.project_name && (
                            <span className="text-xs text-[#594044]/40">
                              📁 {task.project_name}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {completedTasks.length > 3 && (
                      <p className="text-xs text-[#594044]/40 text-center mt-1">
                        +{completedTasks.length - 3} more completed tasks
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <button 
              onClick={() => navigate('/my-tasks')}
              className="w-full mt-4 py-2.5 text-sm text-[#594044] hover:text-[#b50f4a] font-medium transition-all duration-300 bg-gradient-to-r from-lightWhite to-offWhite rounded-full hover:shadow-md border border-royalBlue/10"
            >
              View All Tasks →
            </button>
          </>
        ) : selectedChat ? (
          // Individual Chat View
          <>
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
              <button
                onClick={handleBackToChatList}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-[#594044]" />
              </button>
              <div className="flex-1">
                <h3 className="font-semibold text-[#594044] text-sm truncate">
                  {selectedChat?.title || 'Chat'}
                </h3>
                <p className="text-xs text-[#594044]/40">
                  {chatMessages.length} messages
                </p>
              </div>
            </div>

            {isMessagesLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mainOrange"></div>
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-[#594044]/60">No messages</p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                {chatMessages.map((message, idx) => (
                  <div
                    key={message.id || idx}
                    className={`flex items-start gap-3 ${
                      message.role === 'user' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 mt-1 ${
                      message.role === 'user' ? 'order-2' : ''
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        message.role === 'user'
                          ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                          : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                      }`}>
                        {message.role === 'user' ? (
                          <User className="h-4 w-4 text-white" />
                        ) : (
                          <Bot className="h-4 w-4 text-white" />
                        )}
                      </div>
                    </div>
                    <div className={`flex-1 min-w-0 ${
                      message.role === 'user' ? 'order-1' : ''
                    }`}>
                      <div className={`rounded-2xl p-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-br from-purple-50 to-pink-50'
                          : 'bg-gradient-to-br from-blue-50 to-cyan-50'
                      }`}>
                        <p className="text-sm text-[#594044] whitespace-pre-wrap break-words">
                          {message.content}
                        </p>
                      </div>
                      <p className="text-xs text-[#594044]/30 mt-1">
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          // Chat History List
          <>
            {!isPremium ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center">
                  <Lock className="h-8 w-8 text-yellow-600" />
                </div>
                <p className="text-sm font-medium text-[#594044]">Premium Feature</p>
                <p className="text-xs text-[#594044]/60 mt-1">Upgrade to Premium to view your chat history</p>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="mt-4 px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full hover:shadow-lg transition-all duration-300"
                >
                  Upgrade Now →
                </button>
              </div>
            ) : isChatHistoryLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mainOrange"></div>
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-[#594044]/60">No chat history</p>
                <p className="text-xs text-[#594044]/40 mt-1">Your AI conversations will appear here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {chatHistory.map((chat) => (
                  <div 
                    key={chat.id} 
                    onClick={() => handleChatClick(chat.id)}
                    className="relative flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50/80 transition-all duration-200 cursor-pointer group border border-transparent hover:border-gray-100"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mainPink/20 to-mainOrange/20 flex items-center justify-center">
                        <MessageCircle className="h-4 w-4 text-mainPink" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#594044] text-sm truncate">
                        {chat.title || 'Untitled Chat'}
                      </p>
                      <p className="text-xs text-[#594044]/50 truncate mt-0.5">
                        {chat.last_message || 'No messages'}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[#594044]/40 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getTimeAgo(chat.updated_at)}
                        </span>
                        {chat.message_count > 0 && (
                          <span className="text-xs text-[#594044]/40">
                            {chat.message_count} messages
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenChatMenuId((currentId) =>
                              currentId === chat.id ? null : chat.id
                            );
                          }}
                          disabled={deletingChatId === chat.id}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-[#594044]/35 transition-colors hover:bg-white hover:text-mainPink disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Chat actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {openChatMenuId === chat.id && (
                          <div
                            onClick={(event) => event.stopPropagation()}
                            className="absolute right-0 top-8 z-50 min-w-[120px] rounded-xl border border-gray-100 bg-white p-1 shadow-[0_12px_28px_rgba(89,64,68,0.16)]"
                          >
                            <button
                              type="button"
                              onClick={() => handleDeleteChat(chat.id)}
                              disabled={deletingChatId === chat.id}
                              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              {deletingChatId === chat.id ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-[#594044]/30 group-hover:text-mainPink transition-colors">
                        →
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isPremium && chatHistory.length > 0 && (
              <button 
                onClick={() => navigate('/chats')}
                className="w-full mt-4 py-2.5 text-sm text-[#594044] hover:text-[#b50f4a] font-medium transition-all duration-300 bg-gradient-to-r from-lightWhite to-offWhite rounded-full hover:shadow-md border border-royalBlue/10"
              >
                View All Chats →
              </button>
            )}
          </>
        )}
      </div>

      {/* Frond Tip */}
      <div className="rounded-[40px] p-6 bg-red-300 backdrop-blur-sm shadow-[inset_4px_4px_8px_#e5e2de,inset_-4px_-4px_8px_#ffffff] hover:shadow-[inset_6px_6px_12px_#e5e2de,inset_-6px_-6px_12px_#ffffff] transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#ffd76a] via-[#ff8e5e] to-[#ea3c70] shadow-[0_14px_30px_rgba(234,60,112,0.28),inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform duration-300 hover:scale-110">
            <span className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/35 via-white/10 to-transparent"></span>
            <Lightbulb className="relative z-10 h-6 w-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-blue-950 text-sm mb-1 flex items-center gap-2">
              <span>💡</span> Frond Tip
            </p>
            <p className="text-sm text-[#594044] leading-relaxed">
              {isPremium ? (
                <>You have <span className="font-medium text-[#b50f4a]">{credits} project credits</span> and <span className="font-medium text-orange-600">{chatCredits} chat credits</span>! Use them to create amazing projects and have AI conversations.</>
              ) : hasActiveSubscription ? (
                <>You have <span className="font-medium text-[#b50f4a]">{credits} credits</span> available for projects, tasks, and images. <span className="font-medium text-mainPink">Upgrade to Premium</span> for unlimited AI chat conversations!</>
              ) : (
                <>Try adding <span className="font-medium text-[#b50f4a]">'budget-friendly'</span> or <span className="font-medium text-mainPink">'adventure'</span> to your travel prompts for more tailored itineraries!</>
              )}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default RightSidebar;
